import { AfterContentInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CompanyViewModel,
  CreateFillSiteCommand,
  CreateSourceSiteCommand,
  EditAddressViewModel,
  EditContactViewModel,
  EditFillSiteCommand,
  EditFillSiteSoilQualityTypeViewModel,
  EditSourceSiteCommand,
  ICompanyViewModel,
  ICreateFillSiteCommand,
  ICreateSourceSiteCommand,
  IEditAddressViewModel,
  IEditCompanyCommand,
  IEditContactViewModel,
  IEditFillSiteCommand,
  IEditSourceSiteCommand,
  LegalEntityStatus,
  SiteClassificationType,
  SiteStatus,
  SoilQualityType,
  VisibilityToClients
} from '@services/cleanfill-api/cleanfill-api.generated';
import { ModalService } from '@services/cleanfill-core/modal/modal.service';
import { StoreRegistrationService } from '@services/cleanfill-core/store-registration/store-registration.service';
import { DuplicateCompanyNameValidator } from '@validators/async/duplicate-company-name.validator';
import { allowedFileTypes, maxUploadCount, maxUploadSize, minUploadSize } from '@validators/sync/file-upload.validator';
import { AttachmentFileType } from '@view-models/enums/attachment-file-type.enum';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RegistrationCompleteComponent } from '../modals/registration-complete/registration-complete.component';

@Component({
  selector: 'registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
  providers: [DuplicateCompanyNameValidator]
})
export class RegistrationComponent implements OnInit, OnDestroy, AfterContentInit {
  id: string;
  currentIndex = 1;
  currentForm: AbstractControl;
  company$: Observable<CompanyViewModel>;
  title: string;
  form: FormGroup;
  subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private registrationStore: StoreRegistrationService,
    private modal: ModalService,
    private route: ActivatedRoute,
    private companyNameValidator: DuplicateCompanyNameValidator,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    if (this.route?.snapshot) {
      this.id = this.route.snapshot.params.id;
      this.registrationStore.getCompany(this.id);
    }
    this.initForm();
    this.company$ = registrationStore.company$;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.company$.pipe(filter(comp => !!comp)).subscribe(async comp => {
        if (comp.status === LegalEntityStatus.Active) {
          await this.router.navigateByUrl('/');
        } else {
          this.patchCompany(comp);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  ngAfterContentInit(): void {
    this.cdr.detectChanges();
  }

  initForm(): void {
    this.form = this.fb.group({
      uniqueId: '',
      status: '',
      legalEntityType: '',
      businessType: this.fb.group({
        name: ''
      }),
      companyInfoForm: this.fb.group({
        name: ['', [Validators.required, Validators.maxLength(100)], [this.companyNameValidator.getAsyncValidator(this.id)]],
        address: this.getAddressFormGroup(),
        municipalityName: [''],
        phoneNumber: ['', [Validators.required, Validators.pattern(/^(\+\d{1,2}\s*)?\(?\d{3}\)?[\s.-]*\d{3}[\s.-]*\d{4}$/)]],
        extension: ['', Validators.pattern(/^\d{0,5}$/)]
      }),
      sourceSiteInfo: this.fb.group({
        sites: this.fb.array([])
      }),
      receivingSiteInfo: this.fb.group({
        sites: this.fb.array([])
      }),
      personalInfo: this.fb.group({
        contact: this.getContactFormGroup(),
        billingAsCompanyContact: false,
        billingContact: this.getContactFormGroup()
      })
    });
    const businessType = this.form.get('businessType');
    businessType.markAsTouched();
    this.currentForm = this.form.get('companyInfoForm');
  }

  getSourceSiteFormGroup() {
    return this.fb.group({
      uniqueId: '',
      status: '',
      name: ['', Validators.required],
      address: this.getAddressFormGroup(),
      contact: this.getContactFormGroup(),
      municipalityName: ['', [Validators.maxLength(100)]],
      documents: [
        [],
        [
          maxUploadCount(5),
          minUploadSize(10),
          maxUploadSize(10485760),
          allowedFileTypes(
            AttachmentFileType.DOC,
            AttachmentFileType.DOCX,
            AttachmentFileType.JPEG,
            AttachmentFileType.JPG,
            AttachmentFileType.PDF,
            AttachmentFileType.PNG
          )
        ]
      ]
    });
  }

  getReceivingSiteFormGroup() {
    return this.fb.group({
      uniqueId: '',
      status: '',
      name: ['', [Validators.required, Validators.maxLength(25)]],
      registrationNumber: ['', Validators.maxLength(25)],
      address: this.getAddressFormGroup(),
      contact: this.getContactFormGroup(),
      municipalityName: [''],
      isTPQPApprovalRequired: [false, Validators.required],
      visibilityToClients: [VisibilityToClients.Private, Validators.required],
      siteClassificationType: ['', Validators.required],
      soilQualityTypes: [[], Validators.required],
      documents: [
        [],
        [
          maxUploadCount(5),
          minUploadSize(10),
          maxUploadSize(10485760),
          allowedFileTypes(
            AttachmentFileType.DOC,
            AttachmentFileType.DOCX,
            AttachmentFileType.JPEG,
            AttachmentFileType.JPG,
            AttachmentFileType.PDF,
            AttachmentFileType.PNG
          )
        ]
      ]
    });
  }

  getContactFormGroup() {
    return this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^(\+\d{1,2}\s*)?\(?\d{3}\)?[\s.-]*\d{3}[\s.-]*\d{4}$/)]],
      extension: ['', Validators.pattern(/^\d{0,5}$/)]
    });
  }

  getAddressFormGroup() {
    return this.fb.group({
      postalCode: ['', [Validators.required, Validators.pattern(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/)]],
      line1: ['', Validators.required],
      provinceUniqueId: ['', Validators.required],
      city: ['', Validators.required]
    });
  }
  patchCompany(company: ICompanyViewModel): void {
    const companyInfoPage = {
      companyInfoForm: {
        name: company?.name ?? '',
        address: company?.address ?? '',
        municipalityName: company?.municipalityName ?? '',
        phoneNumber: company?.phoneNumber ?? '',
        extension: company?.extension ?? ''
      },
      personalInfo: {
        billingAsCompanyContact: company?.billingAsCompanyContact ?? false,
        billingContact: company?.billingContact ?? '',
        contact: company?.contact ?? ''
      }
    };
    this.form.patchValue(companyInfoPage);

    if (company?.fillSites.length === 0) {
      this.form.removeControl('receivingSiteInfo');
    } else {
      const sites = this.form.get('receivingSiteInfo').get('sites') as FormArray;
      this.patchReceivingSites(sites, company?.fillSites);
    }

    if (company?.sourceSites.length === 0) {
      this.form.removeControl('sourceSiteInfo');
    } else {
      const sites = this.form.get('sourceSiteInfo').get('sites') as FormArray;
      this.patchSourceSites(sites, company?.sourceSites);
    }

    this.cdr.detectChanges();
  }

  patchSourceSites(formSourceSites: FormArray, companySourceSites) {
    companySourceSites.forEach(site => {
      const formSite = formSourceSites.controls.find(f => f.get('uniqueId').value === site.uniqueId);
      if (formSite) {
        this.registrationStore.getSourceSiteDocuments(site.uniqueId, attachments => {
          formSite.patchValue({ documents: attachments });
        });
        formSite.patchValue({
          ...site,
          address: site.address ?? '',
          contact: site.contact ?? ''
        });
      } else {
        const newSite = this.getSourceSiteFormGroup();
        newSite.patchValue({
          ...site,
          address: site.address ?? '',
          contact: site.contact ?? ''
        });
        formSourceSites.push(newSite);
      }
    });
  }

  patchReceivingSites(formFillSites: FormArray, companyFillSites) {
    companyFillSites.forEach(site => {
      const formSite = formFillSites.controls.find(f => f.get('uniqueId').value === site.uniqueId);
      if (formSite) {
        this.registrationStore.getFillSiteDocuments(site.uniqueId, attachments => {
          formSite.patchValue({ documents: attachments });
        });
        formSite.patchValue({
          ...site,
          visibilityToClients: site.visibilityToClients || VisibilityToClients.Private,
          address: site.address ?? '',
          contact: site.contact ?? ''
        });
      } else {
        const newSite = this.getReceivingSiteFormGroup();
        this.subscriptions.add(
          newSite.get('siteClassificationType').valueChanges.subscribe(value => {
            switch (value) {
              case SiteClassificationType.Class1SoilManagementSite:
              case SiteClassificationType.LandFillingSiteOrDump:
                newSite.patchValue({
                  soilQualityTypes: Object.keys(SoilQualityType).map(
                    key => new EditFillSiteSoilQualityTypeViewModel({ soilQualityType: SoilQualityType[key] })
                  )
                });
                break;
              case SiteClassificationType.ReUseSite:
              case SiteClassificationType.Class2SoilManagementSite:
                newSite.patchValue({
                  soilQualityTypes: [
                    new EditFillSiteSoilQualityTypeViewModel({ soilQualityType: SoilQualityType.OReg153Table1 }),
                    new EditFillSiteSoilQualityTypeViewModel({ soilQualityType: SoilQualityType.OReg153Table2 }),
                    new EditFillSiteSoilQualityTypeViewModel({ soilQualityType: SoilQualityType.OReg153Table3 })
                  ]
                });
                break;
              default:
                break;
            }
          })
        );
        newSite.patchValue({
          ...site,
          address: site.address ?? '',
          contact: site.contact ?? ''
        });
        formFillSites.push(newSite);
      }
    });
  }

  addSourceSite() {
    if (this.form.get('sourceSiteInfo').invalid) {
      return;
    }
    this.saveSourceSites(true);
    const siteModel: ICreateSourceSiteCommand = {
      name: '',
      status: SiteStatus.New
    };
    this.registrationStore.createSourceSite(new CreateSourceSiteCommand(siteModel));
  }

  addReceivingSite() {
    if (this.form.get('receivingSiteInfo').invalid) {
      return;
    }
    this.saveReceivingSites(true);
    const siteModel: ICreateFillSiteCommand = {
      name: '',
      status: SiteStatus.New
    };
    this.registrationStore.createFillSite(new CreateFillSiteCommand(siteModel));
  }
  panelChanged(index: number): void {
    this.saveCurrentForm();
    this.currentIndex = index;

    switch (index) {
      case 0:
        this.router.navigate(['register', 'business-type', 'company', this.id]);
        break;
      case 1:
        this.currentForm = this.form.get('companyInfoForm');
        break;
      case 2:
        this.currentForm = this.form.get('sourceSiteInfo');
        break;
      case 3:
        this.currentForm = this.form.get('receivingSiteInfo');
        break;
      case 4:
        this.currentForm = this.form.get('personalInfo');
        break;
      default:
        break;
    }
  }
  saveCurrentForm() {
    if (this.currentForm.invalid) {
      return;
    }
    switch (this.currentIndex) {
      case 1:
        // save company
        this.saveCompany();
        break;
      case 2:
        // save  source sites
        this.saveSourceSites(true);
        break;
      case 3:
        // save fill sites
        this.saveReceivingSites(true);
        break;
      case 4:
        // save company
        this.saveContact();
        break;
      default:
        break;
    }
  }
  saveSourceSites(isActive = false) {
    // if (this.form.get('sourceSiteInfo').invalid) {
    //   return;
    // }
    if (this.currentForm.untouched) {
      return;
    }
    const formValue = this.form.value;
    const { sourceSiteInfo } = formValue;
    sourceSiteInfo?.sites.forEach(site => {
      const addressModel: IEditAddressViewModel = { ...site.address };
      const contactModel: IEditContactViewModel = { ...site.contact };
      if (site.uniqueId) {
        const siteModel: IEditSourceSiteCommand = {
          ...site,
          address: new EditAddressViewModel(addressModel),
          contact: new EditContactViewModel(contactModel),
          status: isActive ? SiteStatus.Active : SiteStatus.New
        };
        this.registrationStore.editSourceSite(site.uniqueId, new EditSourceSiteCommand(siteModel));
      } else {
        const siteModel: ICreateSourceSiteCommand = {
          ...site,
          address: new EditAddressViewModel(addressModel),
          contact: new EditContactViewModel(contactModel),
          status: SiteStatus.Active
        };
        this.registrationStore.createSourceSite(new CreateSourceSiteCommand(siteModel));
        const sites = this.form.get('sourceSiteInfo.sites') as FormArray;
        sites.clear();
      }
    });
  }

  saveReceivingSites(isActive = false) {
    // if (this.form.get('receivingSiteInfo').invalid) {
    //   return;
    // }
    if (this.currentForm.untouched) {
      return;
    }
    const formValue = this.form.value;
    const { receivingSiteInfo } = formValue;

    receivingSiteInfo?.sites.forEach(site => {
      const addressModel: IEditAddressViewModel = { ...site.address };
      const contactModel: IEditContactViewModel = { ...site.contact };
      if (site.uniqueId) {
        const siteModel: IEditFillSiteCommand = {
          ...site,
          status: isActive ? SiteStatus.Active : SiteStatus.New,
          address: new EditAddressViewModel(addressModel),
          contact: new EditContactViewModel(contactModel)
        };
        this.registrationStore.editFillSite(site.uniqueId, new EditFillSiteCommand(siteModel));
      } else {
        const siteModel: ICreateSourceSiteCommand = {
          ...site,
          address: new EditAddressViewModel(addressModel),
          contact: new EditContactViewModel(contactModel),
          status: SiteStatus.Active
        };
        this.registrationStore.createFillSite(new CreateFillSiteCommand(siteModel));
        const sites = this.form.get('receivingSiteInfo.sites') as FormArray;
        sites.clear();
      }
    });
  }

  saveCompany() {
    // if (this.form.get('companyInfoForm').invalid) return;
    if (this.currentForm.untouched) {
      return;
    }
    const formValue = this.form.value;
    const { companyInfoForm } = formValue;
    const addressModel: IEditAddressViewModel = { ...companyInfoForm.address };
    const companyModel: IEditCompanyCommand = {
      uniqueId: this.id,
      address: new EditAddressViewModel(addressModel),
      name: companyInfoForm.name,
      phoneNumber: companyInfoForm.phoneNumber,
      extension: companyInfoForm.extension,
      municipalityName: companyInfoForm.municipalityName,
      status: LegalEntityStatus.Draft
    };
    this.registrationStore.editCompany(this.id, companyModel);
  }

  saveContact() {
    // if (this.form.get('personalInfo').invalid) return;
    if (this.currentForm.untouched) {
      return;
    }
    const formValue = this.form.value;
    const { personalInfo, companyInfoForm } = formValue;
    const contact: IEditContactViewModel = { ...personalInfo.contact };
    const billingContact: IEditContactViewModel = { ...personalInfo.billingContact };
    const addressModel: IEditAddressViewModel = { ...companyInfoForm.address };

    const companyModel: IEditCompanyCommand = {
      uniqueId: this.id,
      address: new EditAddressViewModel(addressModel),
      name: companyInfoForm.name,
      phoneNumber: companyInfoForm.phoneNumber,
      extension: companyInfoForm.extension,
      municipalityName: companyInfoForm.municipalityName,
      contact: new EditContactViewModel(contact),
      billingAsCompanyContact: personalInfo.billingAsCompanyContact,
      billingContact: new EditContactViewModel(personalInfo.billingAsCompanyContact ? contact : billingContact),
      status: LegalEntityStatus.Draft
    };
    this.registrationStore.editCompany(this.id, companyModel);
  }
  submit(): void {
    if (this.form.invalid) {
      return;
    }
    this.saveSourceSites(true);
    this.saveReceivingSites(true);
    const formValue = this.form.value;
    const { companyInfoForm, personalInfo } = formValue;
    const addressModel: IEditAddressViewModel = { ...companyInfoForm.address };
    const contact: IEditContactViewModel = { ...personalInfo.contact };
    const billingContact: IEditContactViewModel = { ...personalInfo.billingContact };
    const companyModel: IEditCompanyCommand = {
      uniqueId: this.id,
      address: new EditAddressViewModel(addressModel),
      name: companyInfoForm.name,
      phoneNumber: companyInfoForm.phoneNumber,
      extension: companyInfoForm.extension,
      municipalityName: companyInfoForm.municipalityName,
      contact: new EditContactViewModel(contact),
      billingAsCompanyContact: personalInfo.billingAsCompanyContact,
      billingContact: personalInfo.billingAsCompanyContact ? undefined : new EditContactViewModel(billingContact),
      status: LegalEntityStatus.Active
    };
    this.registrationStore.editCompany(this.id, companyModel, async () => {
      this.form.markAsPristine();
      await this.modal.open(RegistrationCompleteComponent, {}).toPromise();
    });
  }
}
